/* eslint-disable no-warning-comments */
import type { NextApiRequest } from 'next';
import env from '@y2/environments';

import { buildAxiosConfigWithCookie, getWithSSRSupport } from '../helpers';
import axios, { ResponseBody } from '../http-client';

export type Yad1Developer = {
  logo: string;
  name: string;
  id: string;
  projectCount: number;
};

// Delete after treedis integration
export type DeveloperPage = {
  name: string;
  info: string;
  logo: string;
  projects: Project[];
};

export type Yad1DevelopersResponse = {
  developers: Yad1Developer[];
  total: number;
};

export type Project = {
  id: string;
  projectName: string;
  address: string;
  coords: { lat: number; lon: number };
  previewType: '3d' | 'combined';
  promotion: boolean;
  promotionalLabel: string;
  mainImage: string;
  secondaryImage: string;
  propertyType: string;
  dealType: 'rent' | 'sale' | 'commercial';
  slug: string;
  developers: ProjectDeveloper[];
  createdAt: string;
  projectLogo: string;
  yad2Id: number;
};

type ProjectDeveloper = {
  name: string;
};

export type GrayProject = {
  id: string;
  developerName: string;
  address: string;
  yad2Id: number;
};

export type ContactDetails = {
  fullName: string;
  projectName: string;
  companyName: string;
  city: string;
  email: string;
  phone: string;
  description?: string;
};

// TODO: replace with real type from treedis
export type AbroadAddressAutocompleteEntity = {
  full_title_text: string;
};

export type WithLeadSource<T> = T & { leadSource: string };

export type GetProjectsSchema = {
  projects: Project[];
  similarProjects: Project[];
  total: number;
};

export type GetGrayProjectsSchema = {
  projects: GrayProject[];
};

export type AbroadAddressAutocomplete = Partial<{
  cities: AbroadAddressAutocompleteEntity[];
  countries: AbroadAddressAutocompleteEntity[];
}>;

export type GeneralQueryParams = Partial<{
  area: string;
  city: string;
  hood: string;
  topArea: string;
  minRooms: number;
  maxRooms: number;
  propertyType: string;
  dealType: string;
  limit: number;
  sale: 1;
  bounds: string;
  page: number;
  abroad: boolean;
}>;

export type ProjectQueryParams = Omit<GeneralQueryParams, 'bounds'>;
export type ProjectBoundsQueryParams = Omit<
  GeneralQueryParams,
  'area' | 'city' | 'hood' | 'topArea'
>;
export type GrayProjectQueryParams = Pick<
  GeneralQueryParams,
  'page' | 'limit' | 'city'
> & { neighborhood?: string };
export type DevelopersQueryParams = Pick<
  GeneralQueryParams,
  'page' | 'limit' | 'topArea' | 'area' | 'city' | 'hood'
>;
export type AbroadProjectsQueryParams = Pick<
  GeneralQueryParams,
  'page' | 'limit' | 'city' | 'minRooms' | 'maxRooms' | 'dealType'
> & { country?: string | undefined };

const serviceName = 'yad1';

/**
 * client for `yad1` service
 * @see https://gw.yad2.io/yad1/api-docs/#
 */
export const yad1Client = (baseUrl = env.routes.api.gw) => {
  const client = axios.create({
    baseURL: getWithSSRSupport(baseUrl, serviceName),
    withCredentials: true,
  });

  return {
    getProjects: (
      query: ProjectQueryParams,
      reqHeaders?: NextApiRequest['headers'],
    ) =>
      client.get<ResponseBody<GetProjectsSchema>>('/projects', {
        params: query,
        ...buildAxiosConfigWithCookie(reqHeaders),
      }),
    getDevelopers: (query?: DevelopersQueryParams) =>
      client.get<ResponseBody<Yad1DevelopersResponse>>('/developers', {
        params: query,
      }),
    getDeveloperById: (id: string) =>
      client.get<ResponseBody<DeveloperPage>>(`/developers/${id}`),
    getProjectsByBounds: (
      query: ProjectBoundsQueryParams,
      reqHeaders?: NextApiRequest['headers'],
    ) =>
      client.get<ResponseBody<GetProjectsSchema>>('/projects/byBounds', {
        params: query,
        ...buildAxiosConfigWithCookie(reqHeaders),
      }),
    getGrayProjects: (query: GrayProjectQueryParams) =>
      client.get<ResponseBody<GetGrayProjectsSchema>>('/gray-projects', {
        params: query,
      }),
    submitContactDetails: (contactDetails: WithLeadSource<ContactDetails>) =>
      client.post('/contact-us', contactDetails),
    getAbroadAddressAutocomplete: () =>
      client.get<ResponseBody<AbroadAddressAutocomplete>>(
        '/abroad-address-autocomplete',
      ),
  };
};
